import { Autocomplete, Button } from "src/components";
import crossIcon from "src/assets/cross.svg";
import { useEffect, useState } from "react";
import { useToast } from "src/hooks/useToast";
import { checkPoStatus, eInvoiceStatusUpdate, getRejectionCategory } from "src/services/invoice-api";
import { useEventStatus } from "src/hooks/useEventStatus";
import { getCookieValue } from "src/constants/functions";

// M10-16235: category 1 e-invoices are rejected together with their FUID, under one of these categories
const CAT1_EINVACK_TYPE = "Upload IRN/BarCode along with Invoice";
const EINVOICE_VALIDATION_HEADER = "E-invoice validation";

interface InvoiceRejectProps {
  onCloseModal: () => void;
  einvoice_dtl_id: number;
  primary_key: number;
  onSuccess: () => void;
  eInvoiceData: any;
}

const InvoiceReject = ({ onCloseModal, einvoice_dtl_id, primary_key, onSuccess, eInvoiceData }: InvoiceRejectProps) => {
  const [remarks, setRemarks] = useState<string>("");
  const [rejectionCategory, setRejectionCategory] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { withEventCheck } = useEventStatus();
  const rejectionReasons = [
    { key: "other", value: "Other" },
    { key: "irn mismatch", value: "IRN Mismatch" },
    { key: "E-invoice duplicate", value: "E-invoice Duplicate" },
  ];

  const { toast } = useToast();

  // M10-16235
  const companyCode = eInvoiceData?.stockistInvoiceAttachment?.company_code;
  const isCategory1 =
    eInvoiceData?.einvoice_attachment?.attachment_type !== "eway" &&
    (eInvoiceData?.einvoice_attachment?.einvoice_cat === CAT1_EINVACK_TYPE ||
      eInvoiceData?.stockistInvoiceAttachment?.einvack_type_code === CAT1_EINVACK_TYPE ||
      eInvoiceData?.stockistInvoiceAttachment?.invoice?.is_category_1 === true);
  const [fuidRejectionCategories, setFuidRejectionCategories] = useState<any[]>([]);
  const [selectedFuidCategory, setSelectedFuidCategory] = useState<any>(null);
  const [step, setStep] = useState<1 | 2>(1);
  // Mandatory inputs of the chosen code (e.g. EIN-002: invoice_number, invoice_date), pre-filled from the invoice
  const [fuidInputs, setFuidInputs] = useState<Record<string, string>>({});
  const fuidInputFields: any[] = (selectedFuidCategory?.mandatory_input_fields ?? []).filter((f: any) => f?.key !== "free_text");

  const selectFuidCategory = (category: any) => {
    setSelectedFuidCategory(category);
    const invoice = eInvoiceData?.stockistInvoiceAttachment?.invoice;
    const invoiceDate = invoice?.new_invoice_date || eInvoiceData?.invoice_date;
    const known: Record<string, string> = {
      invoice_number: invoice?.invoice_number || eInvoiceData?.invoice_number || eInvoiceData?.stockistInvoiceAttachment?.stockist_invoice_number || "",
      invoice_date: invoiceDate ? new Date(invoiceDate).toISOString().split("T")[0] : "",
    };
    setFuidInputs(
      Object.fromEntries((category?.mandatory_input_fields ?? []).map((f: any) => [f.key, known[f.key] ?? ""]))
    );
  };

  useEffect(() => {
    if (!isCategory1) return;
    getRejectionCategory(companyCode)
      .then((res: any) => {
        setFuidRejectionCategories(
          (res?.data?.data ?? [])
            .filter((cat: any) => cat?.rejection_header?.trim().toLowerCase() === EINVOICE_VALIDATION_HEADER.toLowerCase())
            .map((cat: any) => ({ ...cat, display_label: `${cat.rejection_header} - ${cat.rejection_remark}` }))
        );
      })
      .catch(() => toast({ description: "Failed to load rejection categories", variant: "destructive" }));
  }, [isCategory1, companyCode]);

  const fetchCurrentEInvoice = async () => {
    onSuccess();
  };

  // M10-16235: an Entered / Approved category 1 FUID is recalled before it is rejected, so run the same
  // V1 PO / replenishment check the Recall Invoice action does. Returns why the FUID must be left as it
  // is, or undefined when the recall may go ahead.
  const getSkipFuidReason = async (): Promise<string | undefined> => {
    const sia = eInvoiceData?.stockistInvoiceAttachment;
    const fuidStatus = Number(typeof sia?.fileStatusHdr === "object" ? sia?.fileStatusHdr?.fuid_status_id : sia?.fileStatusHdr);
    if (![5, 8].includes(fuidStatus) || !sia?.invoice?.uid) return undefined;
    try {
      const token: any = await getCookieValue("idToken");
      const response = await checkPoStatus({ invoice_seqId: sia.invoice.uid, company_code: companyCode }, token);
      return response?.data?.success == "success" ? undefined : response?.data?.message || "PO / replenishment check failed";
    } catch {
      return "PO / replenishment check could not be completed";
    }
  };

  const handleRejectInvoice = async () => {
    // M10-16235
    if (isCategory1 && !selectedFuidCategory) {
      toast({ description: "Select a rejection category", variant: "destructive" });
      return;
    }
    //M10-11909
    if (!remarks.trim()) {
      toast({ description: "Remarks should not empty", variant: "destructive" });
      return;
    }
    //M10-11909
    setIsLoading(true);
    const skipFuidReason = isCategory1 ? await getSkipFuidReason() : undefined; // M10-16235
    const payload = {
      data: {
        einvoice_dtl_id: einvoice_dtl_id,
        einvoice_status: "rejected",
        remarks: remarks,
        reason: isCategory1 ? "other" : rejectionCategory, // M10-16235: category 1 is classified by fuid_rejection_code
        ...(isCategory1 && { fuid_rejection_code: selectedFuidCategory?.rejection_code }), // M10-16235
        ...(skipFuidReason && { skip_fuid_reason: skipFuidReason }), // M10-16235
        ...(isCategory1 && fuidInputFields.length > 0 && { fuid_mandatory_inputs: fuidInputs }), // M10-16235
        reference_number: remarks,  //M10-11909
        mapped_fuid_number: eInvoiceData?.mapped_fuid_number,
        irn_number: eInvoiceData?.irn_number ? eInvoiceData?.irn_number : '',
        attachment_type: eInvoiceData?.einvoice_attachment?.attachment_type,
        stockist_invoice_attachment_id: primary_key,  //M10-11909
        invoice_id: eInvoiceData?.stockistInvoiceAttachment?.invoice?.invoice_id //M10-11685 - e-inv unblock stockist
      },
      primary_key: primary_key,
      event_type: "E_INVOICE_STATUS_UPDATE",
    };

    try {
      const response = await eInvoiceStatusUpdate(payload, eInvoiceData?.stockistInvoiceAttachment?.company_code);
      console.log("res", response);
      const res = await withEventCheck(() => fetchCurrentEInvoice(), response?.data?.data?.eventStreamCreated?.streamId,eInvoiceData?.stockistInvoiceAttachment?.company_code); //M10-12758
      console.log("res", res);
      toast({ description: "Invoice rejected successfully", variant: "default" });
      // M10-16235: category 1 - backend also rejects (recalling first if entered/approved) the FUID
      const fuidRejection = response?.data?.data?.fuidRejection;
      if (fuidRejection?.message) {
        toast({ description: fuidRejection.message, variant: fuidRejection.action === "skipped" ? "destructive" : "default" });
      }
      onCloseModal();
    } catch (error: any) {
      toast({ description: error?.response?.data?.message, variant: "destructive" });
    } finally {
      setIsLoading(false);
      onCloseModal();
    }
  };

  // M10-16235: category 1 uses the same stepped layout as the FUID rejection dialog (invoice-reject.tsx)
  if (isCategory1) {
    return (
      <div className="flex flex-col gap-y-3 w-[560px] mx-auto bg-white p-5 border border-gray-300 shadow-lg rounded-lg max-h-[90vh]">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <p className="text-base font-semibold text-[#1B1A1F]">Reject E-Invoice</p>
            <p className="text-xs text-[#666666]">Step {step} of 2</p>
          </div>
          <Button className="bg-transparent -mr-3" onClick={onCloseModal}>
            <img src={crossIcon} alt="close" className="w-5 h-5 font-bold invert cursor-pointer" />
          </Button>
        </div>

        {/* Step progress indicator */}
        <div className="flex gap-1">
          {[1, 2].map((s) => (
            <div key={s} className={`h-1 flex-1 rounded-full ${s <= step ? "bg-[#8051FF]" : "bg-gray-200"}`} />
          ))}
        </div>

        {/* ── STEP 1: Select rejection type ── */}
        {step === 1 && (
          <div className="flex flex-col gap-y-3">
            <div>
              <p className="text-sm text-[#333] mb-1">Rejection Header - Rejection Remark</p>
              <Autocomplete
                key={`category-${selectedFuidCategory?.rejection_code || "empty"}`}
                inputClassName="w-full"
                placeholder="Select a rejection"
                suggestionList={fuidRejectionCategories}
                displayKey="display_label"
                uniqueKey="rejection_code"
                defaultValue={selectedFuidCategory ?? undefined}
                onSelectionChange={(e: any) => selectFuidCategory(e || null)}
              />
            </div>

            {selectedFuidCategory && (
              <div className="bg-[#f5f0ff] border border-[#d8caff] rounded-md p-3 text-sm text-[#444]">
                <span className="font-medium text-[#8051FF]">{selectedFuidCategory.rejection_code}</span>
                {selectedFuidCategory.corrective_hint && (
                  <p className="mt-1 text-xs text-[#666]">{selectedFuidCategory.corrective_hint}</p>
                )}
              </div>
            )}

            <Button disabled={!selectedFuidCategory} className="text-white bg-[#8051FF] mt-1" onClick={() => setStep(2)}>
              Next
            </Button>
          </div>
        )}

        {/* ── STEP 2: Remarks & confirm ── */}
        {step === 2 && selectedFuidCategory && (
          <div className="flex flex-col gap-y-3">
            <div className="flex flex-col gap-y-2 max-h-[60vh] overflow-y-auto pr-1">
              <div className="bg-gray-50 rounded-md p-3 text-sm">
                <p className="text-xs text-[#888] mb-1">Rejection Type</p>
                <p className="font-medium text-[#1B1A1F]">
                  {selectedFuidCategory.rejection_header} → {selectedFuidCategory.rejection_remark}
                </p>
                <p className="text-xs text-[#8051FF] mt-1">{selectedFuidCategory.rejection_code}</p>
              </div>

              {selectedFuidCategory.corrective_hint && (
                <div className="bg-[#f5f0ff] border border-[#d8caff] rounded-md p-3 text-sm">
                  <p className="text-xs text-[#8051FF] font-medium mb-1">Remarks/Comments</p>
                  <p className="text-[#444]">{selectedFuidCategory.corrective_hint}</p>
                </div>
              )}

              {/* Required inputs of the chosen code, pre-filled from the invoice */}
              {fuidInputFields.map((field: any) => (
                <div key={field.key}>
                  <p className="text-sm text-[#333] mb-1">{field.label || field.key}</p>
                  <input
                    type={field.type === "date" ? "date" : "text"}
                    value={fuidInputs[field.key] ?? ""}
                    onChange={(e) => setFuidInputs((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    className="w-full p-2 border bg-[#f7f7f7] border-gray-300 rounded-md text-sm"
                  />
                </div>
              ))}

              <div>
                <p className="text-sm text-[#333] mb-1">Remarks</p>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Enter remarks"
                  className="w-full p-2 resize-none border bg-[#f7f7f7] h-[100px] border-gray-300 rounded-md text-sm"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button className="flex-1 bg-gray-100 text-[#333] hover:bg-gray-200" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button
                disabled={isLoading || !remarks.trim() || fuidInputFields.some((f: any) => !fuidInputs[f.key]?.trim())}
                isLoading={isLoading}
                loaderSize="small"
                className="flex-1 text-white bg-[#FF4444]"
                onClick={handleRejectInvoice}
              >
                Confirm Rejection
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-y-2 w-[345px] mx-auto bg-white p-4 border border-gray-300 shadow-lg rounded-xl">
      <div className="flex justify-between items-center">
        <p className="text-normal py-1 font-semibold text-[#1B1A1F]">Reject E-Invoice</p>
        <Button className="bg-transparent -mr-3" onClick={onCloseModal}>
          <img src={crossIcon} alt="cross" className="w-5 h-5 font-bold invert cursor-pointer" />
        </Button>
      </div>
      <p className="text-sm mt-2 font-normal text-[#666666]">Select Rejection Reason</p>
      <Autocomplete
        inputClassName="w-full py-2"
        placeholder="Select Rejection Reason"
        suggestionList={rejectionReasons}
        displayKey="value"
        uniqueKey="key"
        onSelectionChange={(e: any) => setRejectionCategory(e?.key)}
      />
      <p className="text-sm mt-2 font-normal text-[#666666]">Remarks</p>
      {/* MEDVOL: M10-10639 changes */}
      <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder=" " className="p-2 mb-2 resize-none border bg-[#f7f7f7] max-h-[120px] h-[120px] border-gray-300 rounded-md" />
      <div className="flex justify-end gap-x-2 pt-2">
        <Button className="font-normal h-8 bg-gray-200 rounded-md border border-gray-300 shadow-sm" onClick={onCloseModal}>
          Back
        </Button>{" "}
        <Button
          isLoading={isLoading}
          loaderSize="medium"
          className="font-normal text-white h-8 border rounded-md border-[#8051FF] shadow-sm bg-[#8051FF]"
          onClick={handleRejectInvoice}
        >
          Proceed
        </Button>
      </div>
    </div>
  );
};

export default InvoiceReject;
